import React, {useMemo, useRef, useEffect} from 'react';
import CuratedEventContent, {directionsLinkStyles, listItemStyles} from './CuratedEventContent';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../../hooks/useFetchGlobalSearch';
import {useInfiniteMeetups} from '../../../hooks/useMeetUps';
import useGetUserLocation from '../../../hooks/useUserLocation';
import {useOffers} from '../../../hooks/useOffers';
import {calculateDistance, convertFootToMiles} from '../../../utils/distance';
import {SpinnerLoader} from '../../../components/Loader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Event} from '../../../types/article';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TasteEventContentProps {
  topicId?: string | number;
  eventIds?: number[];
  event?: Event;
  targetLocation?: {
    latitude: number;
    longitude: number;
  };
}

const TasteEventContent: React.FC<TasteEventContentProps> = ({
  topicId,
  eventIds = [],
  event,
  targetLocation,
}) => {
  const userLocation = useGetUserLocation();
  const hasEverLoadedData = useRef(false);
  const [accumulatedOffers, setAccumulatedOffers] = React.useState<any[]>([]);
  const [accumulatedMeetups, setAccumulatedMeetups] = React.useState<any[]>([]);
  const [accumulatedEvents, setAccumulatedEvents] = React.useState<any[]>([]);
  const [accumulatedPlaces, setAccumulatedPlaces] = React.useState<any[]>([]);

  // Default to North Park center if no target location provided
  const defaultTargetLocation = {
    latitude: 32.7467,
    longitude: -117.131,
  };

  const finalTargetLocation = targetLocation || defaultTargetLocation;

  // Convert topicId to string for consistency
  const normalizedTopicId = useMemo(() => {
    return topicId ? String(topicId) : '';
  }, [topicId]);

  // Search params for offers, events, and places
  const offersSearchParams = useMemo(
    () => ({
      type: 'offer',
      lat: finalTargetLocation.latitude,
      long: finalTargetLocation.longitude,
      topics: [normalizedTopicId],
      term: '',
      orderBy: 'ends_at',
      sort: {by: 'ends_at', direction: 'asc'},
    }),
    [finalTargetLocation.latitude, finalTargetLocation.longitude, normalizedTopicId]
  );

  const eventSearchParams = useMemo(
    () => ({
      type: 'event',
      lat: finalTargetLocation.latitude,
      long: finalTargetLocation.longitude,
      topics: [normalizedTopicId],
      term: '',
      orderBy: 'distance',
      sort: {by: 'start_date_time', direction: 'asc'},
    }),
    [finalTargetLocation.latitude, finalTargetLocation.longitude, normalizedTopicId]
  );

  const placesSearchParams = useMemo(
    () => ({
      type: 'place',
      lat: finalTargetLocation.latitude,
      long: finalTargetLocation.longitude,
      topics: [normalizedTopicId],
      term: '',
      orderBy: 'default',
      sort: 'distance',
    }),
    [finalTargetLocation.latitude, finalTargetLocation.longitude, normalizedTopicId]
  );

  // Data fetching hooks
  const fetchTasteOffers = useInfiniteGlobalSearchWithParamsAndLocation(offersSearchParams);
  const fetchTasteEvents = useInfiniteGlobalSearchWithParamsAndLocation(eventSearchParams);
  const fetchTastePlaces = useInfiniteGlobalSearchWithParamsAndLocation(placesSearchParams);
  const fetchMeetups = useInfiniteMeetups();

  // Fetch offers for the current event
  const {offers: eventOffers, offersIsLoading} = useOffers({
    articleType: 'eventId',
    articleId: event?.id?.toString(),
    enabled: !!event?.id,
  });

  // Auto-fetch all pages for events until all are loaded
  useEffect(() => {
    if (
      fetchTasteEvents.hasNextPage &&
      !fetchTasteEvents.isFetchingNextPage &&
      !fetchTasteEvents.isLoading
    ) {
      fetchTasteEvents.fetchNextPage();
    }
  }, [
    fetchTasteEvents.hasNextPage,
    fetchTasteEvents.isFetchingNextPage,
    fetchTasteEvents.isLoading,
  ]);

  // Accumulate events in state as new pages are loaded
  useEffect(() => {
    if (!fetchTasteEvents.data?.pages) return;
    const allEvents = fetchTasteEvents.data.pages.flatMap((page: any) => page.data.data || []);
    setAccumulatedEvents((prev) => {
      if (prev.length === allEvents.length) return prev;
      return allEvents;
    });
  }, [fetchTasteEvents.data?.pages]);

  // Auto-fetch all pages for places until all are loaded
  useEffect(() => {
    if (
      fetchTastePlaces.hasNextPage &&
      !fetchTastePlaces.isFetchingNextPage &&
      !fetchTastePlaces.isLoading
    ) {
      fetchTastePlaces.fetchNextPage();
    }
  }, [
    fetchTastePlaces.hasNextPage,
    fetchTastePlaces.isFetchingNextPage,
    fetchTastePlaces.isLoading,
  ]);

  // Accumulate places in state as new pages are loaded
  useEffect(() => {
    if (!fetchTastePlaces.data?.pages) return;
    const allPlaces = fetchTastePlaces.data.pages.flatMap((page: any) => page.data.data || []);
    setAccumulatedPlaces((prev) => {
      if (prev.length === allPlaces.length) return prev;
      return allPlaces;
    });
  }, [fetchTastePlaces.data?.pages]);

  // Auto-fetch all pages for meetups when the first page loads
  useEffect(() => {
    if (fetchMeetups.data && fetchMeetups.hasNextPage && !fetchMeetups.isFetchingNextPage) {
      fetchMeetups.fetchNextPage();
    }
  }, [fetchMeetups.data, fetchMeetups.hasNextPage, fetchMeetups.isFetchingNextPage]);

  // Accumulate meetups in state as new pages are loaded
  useEffect(() => {
    if (!fetchMeetups.data?.pages) return;
    const allMeetups = fetchMeetups.data.pages.flatMap((page: any) => page.data.data || []);
    setAccumulatedMeetups((prev) => {
      if (prev.length === allMeetups.length) return prev;
      return allMeetups;
    });
  }, [fetchMeetups.data?.pages]);

  // Auto-fetch all pages for offers when the first page loads
  useEffect(() => {
    if (
      fetchTasteOffers.data &&
      fetchTasteOffers.hasNextPage &&
      !fetchTasteOffers.isFetchingNextPage
    ) {
      fetchTasteOffers.fetchNextPage();
    }
  }, [fetchTasteOffers.data, fetchTasteOffers.hasNextPage, fetchTasteOffers.isFetchingNextPage]);

  // Accumulate offers as pages load
  useEffect(() => {
    if (!fetchTasteOffers.data?.pages) return;
    const allOffers = fetchTasteOffers.data.pages.flatMap((page: any) => page.data.data) || [];
    setAccumulatedOffers((prev) => {
      if (prev.length === allOffers.length) return prev;
      return allOffers;
    });
  }, [fetchTasteOffers.data?.pages]);

  // Check if all required parameters are valid
  const areParamsValid = useMemo(() => {
    return (
      offersSearchParams.type &&
      offersSearchParams.lat &&
      offersSearchParams.long &&
      eventSearchParams.type &&
      eventSearchParams.lat &&
      eventSearchParams.long &&
      placesSearchParams.type &&
      placesSearchParams.lat &&
      placesSearchParams.long &&
      normalizedTopicId
    );
  }, [offersSearchParams, eventSearchParams, placesSearchParams, normalizedTopicId]);

  // Check if all data is loaded and parameters are valid
  const isDataLoaded = useMemo(() => {
    if (!areParamsValid) {
      return false;
    }

    // console.log('fetchTastePlaces', fetchTastePlaces);
    const allFetchesComplete =
      !fetchTasteOffers.isLoading &&
      !fetchTasteOffers.isFetchingNextPage &&
      !fetchTasteEvents.isLoading &&
      !fetchTasteEvents.isFetchingNextPage &&
      !fetchTastePlaces.isLoading &&
      !fetchTastePlaces.isFetchingNextPage &&
      !fetchMeetups.isLoading &&
      !fetchMeetups.isFetchingNextPage &&
      (!event?.id || !offersIsLoading);

    // Also check if there are no more pages to fetch
    const allPagesLoaded =
      !fetchTasteOffers.hasNextPage &&
      !fetchTasteEvents.hasNextPage &&
      !fetchTastePlaces.hasNextPage &&
      !fetchMeetups.hasNextPage;

    const isFullyLoaded = allFetchesComplete && allPagesLoaded;

    if (isFullyLoaded) {
      hasEverLoadedData.current = true;
    }
    return isFullyLoaded;
  }, [
    areParamsValid,
    fetchTasteOffers.isLoading,
    fetchTasteOffers.isFetchingNextPage,
    fetchTasteOffers.hasNextPage,
    fetchTasteEvents.isLoading,
    fetchTasteEvents.isFetchingNextPage,
    fetchTasteEvents.hasNextPage,
    fetchTastePlaces.isLoading,
    fetchTastePlaces.isFetchingNextPage,
    fetchTastePlaces.hasNextPage,
    fetchMeetups.isLoading,
    fetchMeetups.isFetchingNextPage,
    fetchMeetups.hasNextPage,
    offersIsLoading,
    event?.id,
  ]);

  // Check if this is initial loading (no data yet and currently loading)
  const isInitialLoading = useMemo(() => {
    if (!areParamsValid) {
      return false;
    }
    if (hasEverLoadedData.current) {
      return false;
    }
    const hasAnyData =
      fetchTasteOffers.data || fetchTasteEvents.data || fetchTastePlaces.data || fetchMeetups.data;
    if (hasAnyData && isDataLoaded) {
      hasEverLoadedData.current = true;
      return false;
    }
    if (hasAnyData && !isDataLoaded) {
      return true;
    }
    const isAnyLoading =
      fetchTasteOffers.isLoading ||
      fetchTasteOffers.isFetchingNextPage ||
      fetchTasteEvents.isLoading ||
      fetchTasteEvents.isFetchingNextPage ||
      fetchTastePlaces.isLoading ||
      fetchTastePlaces.isFetchingNextPage ||
      fetchMeetups.isLoading ||
      fetchMeetups.isFetchingNextPage ||
      offersIsLoading;
    return isAnyLoading && !hasEverLoadedData.current;
  }, [
    areParamsValid,
    fetchTasteOffers.data,
    fetchTasteEvents.data,
    fetchTastePlaces.data,
    fetchMeetups.data,
    fetchTasteOffers.isLoading,
    fetchTasteOffers.isFetchingNextPage,
    fetchTasteEvents.isLoading,
    fetchTasteEvents.isFetchingNextPage,
    fetchTastePlaces.isLoading,
    fetchTastePlaces.isFetchingNextPage,
    fetchMeetups.isLoading,
    fetchMeetups.isFetchingNextPage,
    offersIsLoading,
    isDataLoaded,
  ]);

  // Memoized processed data to prevent flickering during incremental loading
  const processedData = useMemo(() => {
    if (!isDataLoaded) {
      return {
        offers: [],
        events: [],
        meetups: [],
        places: [],
      };
    }

    // Process offers
    const mergedOffers = [...accumulatedOffers];
    if (eventOffers && eventOffers.length > 0) {
      mergedOffers.push(...eventOffers);
    }

    const filteredOffers = mergedOffers.filter(() => true); // Topic filtering commented out

    const seenOfferIds = new Set();
    const uniqueOffers = filteredOffers.filter((offer) => {
      if (seenOfferIds.has(offer.id)) {
        return false;
      }
      seenOfferIds.add(offer.id);
      return true;
    });

    const processedOffers = uniqueOffers.map((offer) => ({
      ...offer,
      customCategory: 'offers',
    }));

    // Process events
    const processedEvents = accumulatedEvents
      .filter((event) => {
        if (event.is_meetup === true) {
          return false;
        }
        // Check if event has the topic
        const topicsArray = event.topics_of_interest || event.topics || [];
        const hasTopicId = topicsArray.some((topic) => topic.id == normalizedTopicId);
        return hasTopicId;
      })
      .map((event) => ({
        ...event,
        customCategory: 'events',
        originalType: 'event',
      }));

    // Process meetups
    const processedMeetups = accumulatedMeetups.filter((meetup) => {
      const topicsArray = meetup.topics_of_interest || meetup.topics || [];
      const hasTopicId = topicsArray.some((topic) => topic.id == normalizedTopicId);
      return hasTopicId;
    });

    // Process places
    const processedPlaces = accumulatedPlaces
      .filter((place) => {
        const topicsArray = place.topics_of_interest || place.topics || [];
        const hasTopicId = topicsArray.some((topic) => topic.id == normalizedTopicId);
        return hasTopicId;
      })
      .map((place) => ({
        ...place,
        customCategory: 'places',
        originalType: 'place',
      }));

    return {
      offers: processedOffers,
      events: processedEvents,
      meetups: processedMeetups,
      places: processedPlaces,
    };
  }, [
    isDataLoaded,
    accumulatedOffers.length, // Only depend on length to reduce re-calculations
    accumulatedEvents.length,
    accumulatedMeetups.length,
    accumulatedPlaces.length,
    eventOffers?.length,
    normalizedTopicId,
  ]);

  // Stable data loader that doesn't recreate on every data change
  const customDataLoader = useMemo(() => {
    return {
      loadOffers: () => processedData.offers,
      loadEvents: () => processedData.events,
      loadMeetups: () => processedData.meetups,
      loadPlaces: () => processedData.places,
      loadPartners: () => [],
    };
  }, [processedData]);

  const tasteEventConfig = useMemo(
    () => ({
      title: 'Taste of North Park',
      layout: 'map-and-list' as const,
      directionsLinkStyle: directionsLinkStyles.TEXT,
      listItemStyle: listItemStyles.TEXT,
      mapConfig: {
        enabled: true,
        title: 'Taste of North Park Map',
        center: finalTargetLocation,
        initialZoom: 14,
        height: '275px',
        showUserLocation: true,
      },
      sections: {
        places: {
          checkboxEnabled: true,
          enabled: true,
          title: 'Participating Restaurants',
          icon: '🍽️',
          backgroundColor: '#e74c3c',
          textColor: 'white',
          borderColor: '#e74c3c',
          hoverColor: '#c0392b',
          circleColor: '#e74c3c',
          description: '{count} {count !== 1 ? "restaurants" : "restaurant"}',
          sortBy: 'distance' as const,
        },
        offers: {
          enabled: true,
          title: 'Get Free Tickets',
          icon: '🎫',
          backgroundColor: '#f39c12',
          textColor: 'white',
          borderColor: '#f39c12',
          hoverColor: '#e67e22',
          circleColor: '#f39c12',
          description: '{count} {count !== 1 ? "offers" : "offer"} available',
          sortBy: 'distance' as const,
        },
        events: {
          enabled: true,
          title: 'Events & Activities',
          icon: '🎉',
          backgroundColor: '#9b59b6',
          textColor: 'white',
          borderColor: '#9b59b6',
          hoverColor: '#8e44ad',
          circleColor: '#9b59b6',
          description: '{count} {count !== 1 ? "events" : "event"}',
          sortBy: 'distance' as const,
        },
        meetups: {
          enabled: true,
          title: 'Meetups',
          icon: '👥',
          backgroundColor: '#27ae60',
          textColor: 'white',
          borderColor: '#27ae60',
          hoverColor: '#229954',
          circleColor: '#27ae60',
          description: '{count} {count !== 1 ? "meetups" : "meetup"}',
          sortBy: 'distance' as const,
        },
        partners: {
          enabled: false,
          title: 'Partners',
          icon: '🏢',
          backgroundColor: '#34495e',
          textColor: 'white',
          borderColor: '#34495e',
          hoverColor: '#2c3e50',
          circleColor: '#34495e',
          description: '{count} partner{count !== 1 ? "s" : ""}',
          sortBy: 'distance' as const,
        },
      },
      distanceFilter: {
        enabled: true,
        maxDistance: 5, // 5 mile radius for taste events
        centerLocation: finalTargetLocation,
      },
      dateFilter: {
        enabled: true,
        referenceEvent: event,
        mode: 'same-day' as const,
      },
    }),
    [event, finalTargetLocation]
  );

  return (
    <>
      {isInitialLoading && (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <SpinnerLoader />
        </div>
      )}
      {areParamsValid && !isInitialLoading && (
        <CuratedEventContent
          config={tasteEventConfig}
          customDataLoader={customDataLoader}
          event={event}
        />
      )}
    </>
  );
};

export default TasteEventContent;
